'use client';

import { fetchCustomerAccount } from '@/api/account';
import { toast } from '@/components/Toast';
import { updateConsentsAfterAuth } from '@/hooks/useComplianceConsent';
import useLoginSuccess from '@/hooks/useLoginSuccess';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { IS_US } from '@/utils/constants';
import { getGroupInfo, getProductInfo } from '@/utils/feature-value';
import { isInZeloolApp } from '@/utils/tools';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import {
  reportThirdPartyLoginSuccess,
  runThirdPartyLoginFlow,
  sanitizeThirdPartyLoginUrl,
  type ThirdPartyLoginSuccessProperties,
} from './useThirdPartyLogin.flow';

type ThirdLoginMethodPayload = {
  login_type: string;
  login_method: string;
  sceneCode?: string;
};

interface ThirdLoginParams {
  token?: string;
  store_id?: string;
  isRegister?: string;
  registerType?: string;
  eventType?: string;
  third_login_msg?: string;
}

/**
 * 第三方登录成功处理Hook
 * 迁移自Vue项目的checkThirdLogin方法
 */
export const useThirdPartyLogin = () => {
  const router = useRouter();
  const { handleLoginSuccess } = useLoginSuccess();
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const inAppH5 = useStore((state) => state.inAppH5);
  const groupInfo = getGroupInfo();
  const productInfo = getProductInfo();
  const setToken = useStore((state) => state.setToken);

  /**
   * 神策埋点：登录成功结果
   * 迁移自Vue项目的SDLoginSuccessResult方法
   * @TrackType sensorsdata
   * @EventType LoginSuccess_Result
   * @EventDesc 登录成功结果
   * @EventParams {
   *  is_new: 是否新注册,
   *  login_situation: 登录场景,
   *  url_path: 登录场景的页面路径,
   *  login_type: 登录方式
   *  group_id: 前置分组 id
   *  product_sku: 魔晶商品 SKU
   *  shop_sku： 网站商品 SKU
   * }
   * @Tapd 1008875
   */
  const trackLoginSuccessResult = useCallback(
    (is_new: boolean, login_type: string | undefined, urlPath?: string) => {
      try {
        let thirdLoginType: Partial<ThirdLoginMethodPayload> = {};
        let loginSituationNew = '';

        // 从 sessionStorage 获取登录情况数据
        if (typeof window !== 'undefined') {
          const storedThirdLoginMethod = sessionStorage.getItem('zl-third-login-method');
          if (storedThirdLoginMethod) {
            try {
              thirdLoginType = JSON.parse(storedThirdLoginMethod);
            } catch (e) {
              logger.error('Failed to parse third login method:', e);
            }
          }

          const storedLoginSituationNew = sessionStorage.getItem('zl-login-situation-new');
          if (storedLoginSituationNew) {
            loginSituationNew = storedLoginSituationNew;
          }
        }

        const safeUrlPath =
          urlPath ??
          (typeof window !== 'undefined'
            ? sanitizeThirdPartyLoginUrl(window.location.href).trackingUrl
            : '');
        const sdEventParams: ThirdPartyLoginSuccessProperties = {
          is_new,
          url_path: safeUrlPath,
          login_method: thirdLoginType.login_method || '',
          account_type: '三方账号',
          account_platform: thirdLoginType.login_type || '',
          login_type,
          group_id: groupInfo.group_id,
          product_sku: productInfo.product_sku,
          shop_sku: productInfo.shop_sku,
          page_name: cnName || '其他',
          login_situation_new: loginSituationNew || '其他',
        };

        reportThirdPartyLoginSuccess(trackEvent, sdEventParams);
      } catch (error) {
        logger.error('LoginSuccess_Result tracking error:', error);
      }
    },
    [trackEvent, cnName, groupInfo, productInfo],
  );

  /**
   * 检查第三方登录成功
   * 从URL参数中获取登录信息并处理
   */
  const checkThirdLogin = useCallback(async () => {
    // 获取URL参数的辅助函数
    const getQueryParam = (name: string): string | null => {
      if (typeof window === 'undefined') {
        return null;
      }
      return new URLSearchParams(window.location.search).get(name);
    };

    const params: ThirdLoginParams = {
      token: getQueryParam('token'),
      store_id: getQueryParam('store_id'),
      isRegister: getQueryParam('isRegister'),
      registerType: getQueryParam('registerType'),
      eventType: getQueryParam('eventType'),
      third_login_msg: getQueryParam('third_login_msg'),
    };
    logger.warn('ThirdLoginParams', params);

    try {
      /* 登录成功处理 */
      const token = params.token;
      if (token) {
        // APP 内嵌通过 BackTokenInfo 通道处理登录态，URL 上的 token 参数属于 APP 自带，
        // 这里直接跳过，避免与第三方登录回跳逻辑冲突导致的 401 → performLogout 跳首页问题
        if (isInZeloolApp()) {
          setToken(token);
          return;
        }
        const urlInfo = sanitizeThirdPartyLoginUrl(window.location.href);
        await runThirdPartyLoginFlow({
          urlInfo,
          prepareToken: () => setToken(token),
          fetchUser: async () => {
            const accountResponse = await fetchCustomerAccount();
            return accountResponse.code === 200 && accountResponse.data
              ? accountResponse.data
              : null;
          },
          handleLoginSuccess: (userData) =>
            handleLoginSuccess({
              token,
              customer: userData,
              msg: !params.store_id ? 'Login Success' : '',
            }),
          updateCompliance: IS_US
            ? async (userData) => {
                const raw = sessionStorage.getItem('zl-third-login-method');
                if (!raw) return;
                const parsed = JSON.parse(raw) as ThirdLoginMethodPayload;
                const sceneCode = parsed.sceneCode;
                if (!sceneCode) return;
                delete parsed.sceneCode;
                sessionStorage.setItem('zl-third-login-method', JSON.stringify(parsed));
                await updateConsentsAfterAuth({
                  sceneCode,
                  page: cnName,
                  extra: { email: userData.email },
                });
              }
            : undefined,
          // 三方登录成功后的埋点。注：有另外一部分埋点在 userSlice.ts 中处理。
          trackAppLovin: (userData) =>
            trackEvent(
              {
                eventName: 'sign_up',
                properties: {
                  method: params.registerType,
                  user_id: `${userData.id}`,
                },
              },
              [ProviderType.APPLOVIN],
            ),
          trackSnapchat:
            params.isRegister === '1'
              ? (userData) =>
                  trackEvent(
                    undefined,
                    [ProviderType.SNAPCHAT, ProviderType.SNAPCHAT_CONVERSION],
                    { userInfo: userData },
                    'signup.success',
                  )
              : undefined,
          trackLoginSuccess: !inAppH5
            ? (_userData, trackingUrl) =>
                trackLoginSuccessResult(params.isRegister === '1', params.registerType, trackingUrl)
            : undefined,
          clearUrl: (navigationPath) => router.replace(navigationPath, { scroll: false }),
          onError: (stage, error) => logger.error(`Third party login ${stage} failed:`, error),
        });
      }

      /* 登录失败处理 */
      if (params.third_login_msg) {
        toast.error(params.third_login_msg);
      }
    } catch (error) {
      logger.error('Third party login processing failed:', error);
    }
  }, [cnName, handleLoginSuccess, inAppH5, router, setToken, trackEvent, trackLoginSuccessResult]);

  /**
   * 初始化时检查第三方登录 TODO 在 GlobalDialog 里面调用这里的方法
   */
  // useEffect(() => {
  //   // 检查是否有第三方登录相关参数
  //   const hasThirdLoginParams = searchParams.has('token') || searchParams.has('third_login_msg');

  //   if (hasThirdLoginParams) {
  //     checkThirdLogin();
  //   }
  // }, [checkThirdLogin, searchParams]);

  return {
    checkThirdLogin,
    trackLoginSuccessResult,
  };
};

export default useThirdPartyLogin;
